import { isValidElement, type ComponentPropsWithRef, type ReactElement, type ReactNode } from 'react';
import type { ButtonVariantsProps } from '@/shared-ui/atoms/button/button.variants';
import type { TypographyProps } from '@/shared-ui/atoms/typography';

export type ContentCardTextAlign = 'left' | 'center';

export type ContentCardTypographyWeight = TypographyProps['weight'];

export type ContentCardButtonVariant = Exclude<ButtonVariantsProps['variant'], 'purchase-priced'>;

export interface ContentCardButtonConfig {
    label: string;
    /** React Router `to` value passed to shared-ui `Link` in stories. */
    to: string;
    targetWindow?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
    /** Button visual variant for the visible CTA (defaults to `tertiary`). */
    variant?: ContentCardButtonVariant;
    /** When true, the visible CTA button spans the full width of the card content area. */
    fullWidth?: boolean;
    /** Additional margin above the CTA (px number or CSS length). */
    marginTop?: number | string;
}

export interface ContentCardProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
    imageSrc?: string;
    imageAlt?: string;
    slug?: string;
    slugType?: '1' | '2' | '3';
    iconSrc?: string;
    iconAlt?: string;
    subtitle?: string;
    subtitleVariant?: TypographyProps['variant'];
    heading?: string;
    headingVariant?: TypographyProps['variant'];
    subheading?: string;
    subheadingVariant?: TypographyProps['variant'];
    copy?: string;
    copyVariant?: TypographyProps['variant'];
    button?: ContentCardButtonConfig;
    /**
     * CTA button variant for Storybook controls (merged into `button` when rendering).
     * `buttonVariant` wins when both are set (Storybook control).
     */
    buttonVariant?: ContentCardButtonVariant;
    showButton?: boolean;
    width?: number | string;
    /** Horizontal alignment for slug, typography, and CTA (defaults to `left`). */
    textAlign?: ContentCardTextAlign;
    /** Card background as a CSS colour (e.g. `#f6f2ed`, `rgb(...)`, `transparent`). Defaults to transparent. */
    backgroundColor?: string;
    /** When false, the card image uses square corners. Default `true`. */
    rounded?: boolean;
    /** Height for the optional icon / SVG image (e.g. `32`, `2rem`). */
    iconHeight?: number | string;
    /** Additional margin below the icon (px number or CSS length). Passed to `ContentCard.Icon`. */
    iconMarginBottom?: number | string;
    /** Gap between elements in the content area below the image (px number or CSS length). Default `16`. */
    contentGap?: number | string;
    /** Padding around content below the image (px number or CSS padding value). Default `0`. */
    contentPadding?: number | string;
    /** Padding above the content body (below the image), in px. `0` = none. Passed to `ContentCard.Root`. */
    contentBodyPaddingTop?: number | string;
    /** Passed to `ContentCard.Subtitle` `weight`. */
    subtitleWeight?: ContentCardTypographyWeight;
    /** Passed to `ContentCard.Title` `weight`. */
    titleWeight?: ContentCardTypographyWeight;
    /** Passed to `ContentCard.Subheading` `weight`. */
    subheadingWeight?: ContentCardTypographyWeight;
    /** Passed to `ContentCard.Description` `weight`. */
    copyWeight?: ContentCardTypographyWeight;
    /** Storybook control: full-width CTA (merged into `button.fullWidth` when set). */
    ctaFullWidth?: boolean;
    /** Storybook control: margin above the CTA (merged into `button.marginTop` when set). */
    ctaMarginTop?: number | string;
}

/** Normalises spacing props (gap / padding) to a CSS length string. */
export function resolveContentCardSpacing(value?: number | string): string | undefined {
    if (value === undefined) {
        return undefined;
    }
    return typeof value === 'number' ? `${value}px` : value;
}

/** Returns a CSS background value when `backgroundColor` is a non-empty, non-transparent colour. */
export function resolveContentCardBackground(backgroundColor?: string): string | undefined {
    const trimmed = backgroundColor?.trim();
    if (!trimmed || trimmed.toLowerCase() === 'transparent') {
        return undefined;
    }
    return trimmed;
}

export type ContentCardCtaOverlayState = {
    href: string;
    label: string;
    targetWindow?: string;
    rel?: string;
};

/** Concatenates visible text from link children (mirrors React text-node composition). */
function getContentCardCtaLabelText(children: ReactNode): string {
    if (children == null || typeof children === 'boolean') {
        return '';
    }
    if (typeof children === 'string' || typeof children === 'number') {
        return String(children);
    }
    if (Array.isArray(children)) {
        return children.map(getContentCardCtaLabelText).join('');
    }
    if (isValidElement(children)) {
        return getContentCardCtaLabelText((children.props as { children?: ReactNode }).children);
    }
    return '';
}

/** Extracts visible text from link children for overlay `aria-label` fallbacks. */
export function getContentCardCtaLabel(children: ReactNode): string | undefined {
    const text = getContentCardCtaLabelText(children).trim();
    return text.length > 0 ? text : undefined;
}

type ContentCardCtaToLike = {
    pathname?: string;
    search?: string;
    hash?: string;
};

/** Resolves a string href from slotted link props (`to` string, `to` object, or `href`). */
export function resolveContentCardCtaHref(props: Record<string, unknown>): string | undefined {
    const { to, href } = props;

    if (typeof to === 'string' && to.length > 0) {
        return to;
    }
    if (typeof href === 'string' && href.length > 0) {
        return href;
    }
    if (to && typeof to === 'object') {
        const toObject = to as ContentCardCtaToLike;
        const pathname = typeof toObject.pathname === 'string' ? toObject.pathname : '';
        if (pathname.length === 0) {
            return undefined;
        }
        const search = typeof toObject.search === 'string' ? toObject.search : '';
        const hash = typeof toObject.hash === 'string' ? toObject.hash : '';
        return `${pathname}${search}${hash}`;
    }
    return undefined;
}

/** Resolves overlay target/rel from `targetWindow` or native anchor `target` / `rel`. */
export function resolveContentCardCtaTargetRel(props: Record<string, unknown>): {
    targetWindow?: string;
    rel?: string;
} {
    const targetWindow =
        typeof props.targetWindow === 'string' && props.targetWindow.length > 0
            ? props.targetWindow
            : typeof props.target === 'string' && props.target.length > 0
              ? props.target
              : undefined;
    const rel = typeof props.rel === 'string' && props.rel.length > 0 ? props.rel : undefined;
    return { targetWindow, rel };
}

/** Builds overlay state from a slotted `<Link>` or anchor child (`to` / `href`). */
export function resolveContentCardCtaOverlayFromElement(element: ReactElement): ContentCardCtaOverlayState | null {
    const props = element.props as Record<string, unknown>;
    const href = resolveContentCardCtaHref(props);
    const ariaLabel = props['aria-label'];
    const label =
        (typeof ariaLabel === 'string' && ariaLabel.length > 0 ? ariaLabel : undefined) ??
        getContentCardCtaLabel(props.children as ReactNode);
    if (!href || !label) {
        return null;
    }
    const { targetWindow, rel } = resolveContentCardCtaTargetRel(props);
    return {
        href,
        label,
        targetWindow,
        rel,
    };
}
